/**
 * PlatformConsole — iter80
 *
 * Dedicated route + chrome for platform-tier identities (platform_admin,
 * platform_developer). Completely separate from /admin (the tenant Site
 * Admin surface).
 *
 * What this page does:
 *   - Its OWN login form (rejects tenant_admin identities at the boundary).
 *   - Its OWN header — "PLATFORM CONSOLE" branding, no Home/What's New
 *     tenant chrome.
 *   - Its OWN nav — 4 top-level items (iter86.10): Rhythm
 *     (standalone), Operations ▾ (Product Ops, Tenant Ops, Compliance Ops),
 *     Governance ▾ (Values, Obligations, Stewardship, Developer Agency),
 *     Console ▾ (Docs, Release Notes, Platform Roles, Account).
 *
 * Identity routing rules:
 *   - /platform login → JWT issued from platform_users → stays here.
 *   - If a tenant_admin tries to log in here, they get redirected to /admin
 *     with a toast explaining why.
 *   - If a platform user tries to log in at /admin, they get a similar
 *     redirect (handled separately in SiteAdmin.jsx).
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, adminSession } from "@/lib/api";
import { toast } from "sonner";
import {
    Globe, Megaphone, FileText, Crown, Key, LogOut, ShieldCheck,
    Layers, Scale, Gamepad2, Settings, Bot, ChevronDown,
    AudioWaveform, Landmark, ScrollText, Music4,
    Radio, BookOpenCheck, Handshake, Map as MapIcon, HeartPulse, Eye, RefreshCw, Smartphone, ExternalLink, Wrench, MessageCircleWarning,
    ShieldAlert,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import ComplianceTab from "@/components/site-admin/ComplianceTab";
import AtlasTab from "@/components/site-admin/AtlasTab";
import FeaturesTab from "@/components/site-admin/FeaturesTab";
import VendorRegistrySection from "@/components/site-admin/compliance/VendorRegistrySection";
import TotpCard from "@/components/admin/TotpCard";
import TotpChallengeStep from "@/components/admin/TotpChallengeStep";


// iter84.59.5 — Defensive error-message extractor. FastAPI's
// `RequestValidationError` returns `detail` as a LIST of error objects
// `[{type, loc, msg, input, ctx}, ...]`. Passing that array straight to
// `toast.error()` (or rendering it in JSX) crashes React with "Objects
// are not valid as a React child". Coerce to a clean string here so
// every catch block can just do `toast.error(_extractErr(err, "..."))`.
function _extractErr(err, fallback) {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        // Pydantic validation error envelope. Pull each item's `.msg`
        // (and `.loc` if present) for a human-readable single string.
        const parts = detail
            .map((d) => {
                if (typeof d === "string") return d;
                if (d && typeof d === "object") {
                    const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : null;
                    const m = typeof d.msg === "string" ? d.msg : "Invalid input";
                    return loc ? `${loc}: ${m}` : m;
                }
                return null;
            })
            .filter(Boolean);
        if (parts.length) return parts.join("; ");
    }
    if (detail && typeof detail === "object" && typeof detail.msg === "string") {
        return detail.msg;
    }
    if (err?.message && typeof err.message === "string") return err.message;
    return fallback;
}

// Reuse the platform-scoped tabs from /admin (they already exist and work)
import ReleaseOpsTab from "@/components/site-admin/ReleaseOpsTab";
import ConcertHealthTab from "@/components/site-admin/ConcertHealthTab";
import ValuesTab from "@/components/site-admin/ValuesTab";
import ObligationsTab from "@/components/site-admin/ObligationsTab";
import GameOpsTab from "@/components/site-admin/GameOpsTab";
import AdminHealthTab from "@/components/site-admin/AdminHealthTab";
import MobileTestersTab from "@/components/site-admin/MobileTestersTab";
import DevOpsTab from "@/components/site-admin/DevOpsTab";
import StewardshipTab from "@/components/site-admin/StewardshipTab";
import PlatformTenantsTab from "@/components/site-admin/PlatformTenantsTab";
import ReleaseNotesTab from "@/components/site-admin/ReleaseNotesTab";
import DocsTab from "@/components/site-admin/DocsTab";
import PlatformTechDebtTab from "@/components/site-admin/PlatformTechDebtTab";
import IssuesQueueCard from "@/components/site-admin/IssuesQueueCard";
import PlatformRolesTab from "@/components/site-admin/PlatformRolesTab";
import PulseTab from "@/components/site-admin/PulseTab";
import SecurityPostureTab from "@/components/site-admin/SecurityPostureTab";

// (No standalone localStorage keys needed — adminSession from api.js handles
// session persistence shared with /admin.)

function LoginCard({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotBusy, setForgotBusy] = useState(false);
    // iter87.199.43 — TOTP challenge state. When the password-only
    // /auth/login returns `requires_2fa: true`, we render the challenge
    // step in-place of the login form until the second-step succeeds.
    const [challenge, setChallenge] = useState(null);

    const finishLogin = (r) => {
        if (r.admin?.identity_source !== "platform_users") {
            toast.error("That account is a tenant Site Admin — sign in at /admin instead.");
            api.authLogout?.();
            return;
        }
        adminSession.save(null, r.admin);
        onSuccess(r.admin);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.authLogin({
                email: email.trim(),
                password,
            });
            if (r?.requires_2fa) {
                setChallenge({
                    challenge_token: r.challenge_token,
                    expires_in: r.expires_in,
                    admin_email: r.admin_email,
                });
                setBusy(false);
                return;
            }
            finishLogin(r);
        } catch (err) {
            toast.error(_extractErr(err, "Login failed"));
        } finally {
            setBusy(false);
        }
    };

    const sendForgot = async (e) => {
        e.preventDefault();
        if (!email.trim()) { toast.error("Enter your email above first"); return; }
        setForgotBusy(true);
        try {
            const r = await api.authRequestPasswordReset(email.trim());
            toast.success(r.message || "If that email matches a registered account, a reset link is on its way.");
            setForgotMode(false);
        } catch (err) {
            // Should never happen — endpoint always returns 200 to prevent enumeration
            toast.error("Something went wrong. Try again in a minute.");
        } finally {
            setForgotBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-black">
            <div className="brutal-card p-6 max-w-md w-full border-2 border-volt/40" data-testid="platform-login-card">
                <div className="flex items-center gap-2 mb-1">
                    <Landmark className="w-5 h-5 text-volt" />
                    <h1 className="font-display font-extrabold text-2xl uppercase tracking-wider">Commons Console</h1>
                </div>
                <p className="text-xs text-zinc-500 mb-5">For platform operators only. Tenant Site Admins sign in at <span className="font-mono text-cyber">/admin</span>.</p>
                {challenge ? (
                    <TotpChallengeStep
                        challenge={challenge}
                        onSuccess={finishLogin}
                        onCancel={() => setChallenge(null)}
                    />
                ) : (
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase text-zinc-400 tracking-wider">Email</label>
                        <input
                            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            data-testid="platform-login-email-input"
                            className="brutal-input w-full mt-1" autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase text-zinc-400 tracking-wider">Password</label>
                        <input
                            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="platform-login-password-input"
                            className="brutal-input w-full mt-1"
                        />
                    </div>
                    <button
                        type="submit" disabled={busy}
                        data-testid="platform-login-submit-btn"
                        className="brutal-btn w-full bg-volt text-black hover:bg-volt/90 disabled:opacity-50"
                    >
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                </form>
                )}

                {!challenge && !forgotMode ? (
                    <button
                        type="button"
                        onClick={() => setForgotMode(true)}
                        data-testid="platform-forgot-password-link"
                        className="mt-4 text-xs text-zinc-400 hover:text-volt underline"
                    >
                        Forgot password?
                    </button>
                ) : !challenge && (
                    <div className="mt-4 p-3 border-2 border-cyber/30 bg-cyber/5" data-testid="platform-forgot-password-panel">
                        <p className="text-[10px] text-zinc-400 mb-2">Enter your email above, then click Send. We'll email a magic link if it matches a registered account.</p>
                        <div className="flex gap-2">
                            <button type="button" onClick={sendForgot} disabled={forgotBusy} data-testid="platform-forgot-submit-btn" className="brutal-btn text-xs py-1.5 px-3 flex-1">
                                {forgotBusy ? "Sending…" : "Send reset link"}
                            </button>
                            <button type="button" onClick={() => setForgotMode(false)} className="brutal-btn-ghost text-xs py-1.5 px-3">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function AcceptInviteCard({ token, onAccepted }) {
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            await api.platformAcceptInvite(token, password);
            toast.success("Account created — please sign in");
            onAccepted();
            navigate("/platform", { replace: true });
        } catch (err) {
            toast.error(_extractErr(err, "Failed to accept invite"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-black">
            <div className="brutal-card p-6 max-w-md w-full border-2 border-volt/40" data-testid="platform-accept-invite-card">
                <div className="flex items-center gap-2 mb-1">
                    <Landmark className="w-5 h-5 text-volt" />
                    <h1 className="font-display font-extrabold text-xl uppercase tracking-wider">Set up your Commons Console</h1>
                </div>
                <p className="text-xs text-zinc-500 mb-5">Choose a password and you're in.</p>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase text-zinc-400">New password</label>
                        <input
                            type="password" required minLength={8}
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="accept-invite-password-input"
                            className="brutal-input w-full mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase text-zinc-400">Confirm</label>
                        <input
                            type="password" required minLength={8}
                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                            data-testid="accept-invite-confirm-input"
                            className="brutal-input w-full mt-1"
                        />
                    </div>
                    <button type="submit" disabled={busy} data-testid="accept-invite-submit-btn" className="brutal-btn w-full bg-volt text-black hover:bg-volt/90 disabled:opacity-50">
                        {busy ? "Creating…" : "Create account & sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
}

// iter86.22 — "deployed 3 days ago" label for the data-plane canon freshness chip.
function timeAgo(iso) {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 90) return "just now";
    const m = Math.round(s / 60);
    if (m < 90) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 36) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? "" : "s"} ago`;
}

// iter86.27 — Commons legend: one-liner per top-level tab, top-right dropdown.
function ConsoleLegend({ dataPlane, showDormant, onToggleDormant, dormantBusy }) {
    const [open, setOpen] = useState(false);
    const rows = dataPlane ? [
        // iter86.31.15 — Concert plane shows only the tabs that exist there.
        ["Concert Health", "Live production posture — vendor + game health"],
        ["Concert Ops", "Live platform operations"],
        ["Mezzanine", "Release notes, vendors, roles & account plumbing"],
    ] : [
        ["Rhythm", "Release gates: verify on dev, deploy, then PROMOTE (merge dev → main)"],
        ["Rehearsal Ops", "Preview operational test controls"],
        // iter87.64 — Pulse retired from default nav; entry removed from Legend.
        // Deep-link `?tab=pulse` still reaches the archive; see marie-kondo-audit §1.3.
        // iter87.69 — Maestro flipped dormant; entry removed from Legend to
        // match the reduced surface. Deep-link `?tab=stewardship` still reaches
        // it (with a DormantBanner). Canon copy narrowed to Reference Docs —
        // its other sub-tabs (Values / Obligations / Features / Security
        // Posture / Atlas / Developer Agency) are all dormant for the 30-day
        // observation window.
        ["Canon", "Reference Docs — the operator's own reading surface (BRD / FRD / TRD / CHANGELOG)"],
        ["Mezzanine", "Vendors, roles & account plumbing"],
    ];
    return (
        <div className="relative">
            <button onClick={() => setOpen((v) => !v)}
                data-testid="console-legend-btn"
                className="brutal-btn-ghost text-xs px-2.5 py-1 border border-zinc-700 text-zinc-300">
                Legend
            </button>
            {open && (
                <div className="fixed inset-x-3 top-20 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 z-50 sm:w-80 bg-zinc-950 border border-zinc-700 rounded shadow-xl p-3 space-y-2 max-h-[70dvh] overflow-y-auto"
                    data-testid="console-legend-panel">
                    <p className="text-[10px] font-extrabold uppercase text-zinc-400">What each tab is</p>
                    {rows.map(([name, desc]) => (
                        <div key={name} className="text-[11px]">
                            <span className="font-bold text-white">{name}</span>
                            <span className="text-zinc-400"> — {desc}</span>
                        </div>
                    ))}
                    {/* iter87.65 — Dormant surfaces toggle. Persists per-user via
                        PUT /platform-console/preferences. Rehearsal-plane only —
                        the concert plane has no dormant surfaces to reveal. */}
                    {!dataPlane && onToggleDormant && (
                        <div className="border-t border-zinc-800 pt-2 mt-2">
                            <label className="flex items-start gap-2 text-[11px] cursor-pointer" data-testid="console-legend-dormant-toggle-row">
                                <input
                                    type="checkbox"
                                    checked={!!showDormant}
                                    onChange={(e) => onToggleDormant(e.target.checked)}
                                    disabled={dormantBusy}
                                    data-testid="console-legend-dormant-toggle"
                                    className="mt-0.5"
                                />
                                <span>
                                    <span className="font-bold text-white">Show dormant surfaces</span>
                                    <span className="text-zinc-500 block text-[10px] mt-0.5">
                                        Reveals surfaces the marie-kondo audit and 50-percent-cut
                                        experiment (iter87.69) flagged as dormant — Tenant Ops ·
                                        Atlas · Developer Agency · Values · Obligations · Features ·
                                        Security Posture · Maestro · Release Notes.
                                        Preference saved per-user.
                                    </span>
                                </span>
                            </label>
                        </div>
                    )}
                    <p className="text-[9px] text-zinc-600 border-t border-zinc-800 pt-1.5">
                        Rehearsal Plane (preview) = where governance decisions are authored and flow to git (branch: dev).
                        Concert Plane (production) = the live show — read-only canon mirror + player telemetry.
                        Canon = the main branch; it only changes via a PROMOTE merge.
                    </p>
                </div>
            )}
        </div>
    );
}

// iter87.65 — Rendered above a dormant tab's body. Reactivation is a
// Rhythm-gated change (edit NAV entry + PR + promote), not a runtime toggle.
function DormantBanner() {
    return (
        <div
            data-testid="dormant-status-banner"
            className="brutal-card p-3 border-2 border-amber-700/60 bg-amber-950/20 text-xs text-amber-100 flex items-start gap-2"
        >
            <span className="pill text-[9px] font-bold uppercase border bg-amber-900/50 text-amber-200 border-amber-700 shrink-0">Dormant</span>
            <span>
                This surface is dormant — no active operator attention allocated. Reactivate by removing dormant status via a Rhythm-gated change.
            </span>
        </div>
    );
}

export default function PlatformConsole() {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [admin, setAdmin] = useState(null);
    const [bootChecking, setBootChecking] = useState(true);
    // iter87.61 Phase 0 · Step 1 — sub-tab URL persistence.
    // `?tab=<key>` restores the top-level tab across reloads / deep-links.
    // A missing / unrecognised `?tab=` falls through to the tab router's
    // existing default (release_ops on rehearsal plane; the visible-nav
    // fallback handles concert plane).
    const _initialTab = searchParams.get("tab") || "release_ops";
    const [tab, _setTab] = useState(_initialTab);
    const setTab = useCallback((next) => {
        _setTab(next);
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (next) p.set("tab", next); else p.delete("tab");
            // Every tab change resets the sub-tab param — a stale `?sub=`
            // from another tab must not bleed into the new surface.
            p.delete("sub");
            return p;
        }, { replace: false });
    }, [setSearchParams]);
    // iter84.72 — Deep-link target for Game Ops (set by Rhythm sweep rows).
    const [gameOpsTarget, setGameOpsTarget] = useState(null);
    // iter86.58 — Canon → Features vendor links jump here with a highlight.
    const [vendorsTarget, setVendorsTarget] = useState(null);
    // iter84.3 — Drift Watch badge: number of tournaments with drift in
    // the most-recent scan. Surfaces a red dot on the Rhythm tab so
    // operators see "needs attention" before they even click in.
    const [driftCount, setDriftCount] = useState(0);
    // iter86.20 — Pulse badge: viewer's unreviewed subscribed briefing sections.
    const [pulseCount, setPulseCount] = useState(0);
    // iter87.105 — Issues unread badge (admin only; endpoint 403s for other roles).
    const [issuesUnread, setIssuesUnread] = useState(0);
    // iter86.21.5 — control-plane/data-plane identity (preview = authoring, prod = read-only mirror)
    const [envInfo, setEnvInfo] = useState(null);
    // iter86.72 — Refresh-in-place: bumping viewKey remounts the active tab
    // body (each tab refetches on mount), so no full-page reload is needed.
    const [viewKey, setViewKey] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const refreshView = useCallback(() => {
        setRefreshing(true);
        setViewKey((k) => k + 1);
        setTimeout(() => setRefreshing(false), 500);
    }, []);
    useEffect(() => {
        if (!admin) return;
        api.platformEnvironment().then(setEnvInfo).catch(() => {});
    }, [admin]);

    // iter87.65 — Commons Console per-user preference: reveal surfaces the
    // marie-kondo audit flagged as dormant (§7 Q2 ratification landed at
    // iter87.65 for: Tenant Ops, Compliance Ops, Atlas, Developer Agency,
    // Release Notes). Defaults to false so daily attention isn't taxed;
    // toggle lives in the header Legend and persists per-user.
    const [showDormant, setShowDormant] = useState(false);
    const [dormantBusy, setDormantBusy] = useState(false);
    useEffect(() => {
        if (!admin) return;
        api.platformConsolePrefs()
            .then((prefs) => setShowDormant(!!prefs?.show_dormant))
            .catch(() => {});
    }, [admin]);
    const toggleShowDormant = useCallback((next) => {
        const target = !!next;
        setShowDormant(target); // optimistic
        setDormantBusy(true);
        api.platformConsolePrefsUpdate({ show_dormant: target })
            .then((prefs) => setShowDormant(!!prefs?.show_dormant))
            .catch(() => {
                setShowDormant(!target); // rollback on failure
                toast.error("Couldn't save preference — showing local state only");
            })
            .finally(() => setDormantBusy(false));
    }, []);
    // Boot: see if we already have a session
    useEffect(() => {
        api.authMe()
            .then((me) => {
                if (["platform_admin", "platform_developer", "platform_observer"].includes(me?.role)) {
                    setAdmin(me);
                }
            })
            .catch(() => {})
            .finally(() => setBootChecking(false));
        // iter85.3 — session expiry stays on /platform (own login screen)
        const onUnauth = () => setAdmin(null);
        window.addEventListener("admin-unauthorized", onUnauth);
        return () => window.removeEventListener("admin-unauthorized", onUnauth);
    }, []);

    // Poll the last drift scan summary so the tab badge stays current.
    // Platform-admin only — developers don't have the endpoint.
    useEffect(() => {
        if (admin?.role !== "platform_admin") return;
        let cancelled = false;
        const pull = async () => {
            try {
                const r = await api.driftWatchLastScan();
                if (!cancelled) setDriftCount(r?.tournaments_with_drift || 0);
            } catch (_e) { /* silent — non-critical UX */ }
        };
        pull();
        // Refresh every 5 min while the console is open so the badge
        // reflects the hourly background loop's most recent pass.
        const t = setInterval(pull, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [admin]);

    // iter86.20 — Pulse unreviewed badge (both roles).
    useEffect(() => {
        if (!admin) return;
        let cancelled = false;
        const pull = async () => {
            try {
                const r = await api.pulseBadge();
                if (!cancelled) setPulseCount(r?.unreviewed || 0);
            } catch (_e) { /* silent — non-critical UX */ }
        };
        pull();
        const t = setInterval(pull, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [admin]);

    // iter87.105 — Issues unread badge (admin roles only; the endpoint 403s
    // for anyone below platform_admin/developer). Polls every 2 min.
    useEffect(() => {
        if (!admin) return;
        if (admin.role !== "platform_admin" && admin.role !== "platform_developer") return;
        let cancelled = false;
        const pull = async () => {
            try {
                const r = await api.getIssuesBadge();
                if (!cancelled) setIssuesUnread(r?.unread || 0);
            } catch (_e) { /* silent — non-critical UX */ }
        };
        pull();
        const t = setInterval(pull, 2 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [admin]);

    const logout = useCallback(async () => {
        try { await api.authLogout?.(); } catch (_e) { /* best-effort logout */ }
        try { adminSession.clear(); } catch (_e) { /* localStorage unavailable */ }
        setAdmin(null);
    }, []);

    // Invite acceptance flow — /platform/accept/:token
    if (params?.token) {
        return <AcceptInviteCard token={params.token} onAccepted={() => setAdmin(null)} />;
    }

    if (bootChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <p className="text-xs text-zinc-500">Loading Commons Console…</p>
            </div>
        );
    }

    if (!admin) {
        return <LoginCard onSuccess={setAdmin} />;
    }

    // iter86.10 IA: 4 top-level nav items — Rhythm (standalone) |
    // Operations ▾ (Product Ops, Tenant Ops, Compliance Ops) |
    // Governance ▾ (Values, Obligations, Stewardship, Developer Agency) |
    // Console ▾ (Docs, Release Notes, Platform Roles, Account).
    // Internal tab keys (game_ops, dev_ops, …) stay stable — labels only.
    // iter86.31.15 — Commons roles: Observer sees everything (read-only,
    // backend write-guard enforces); Commons Roles is Owner-only territory.
    const BOTH = ["platform_admin", "platform_developer", "platform_observer"];
    const ADMIN = ["platform_admin", "platform_observer"];
    // iter86.27 — steward rename: Rhythm (release gates); Operations label is
    // env-aware (Rehearsal = preview test controls; Concert = live production).
    const opsLabel = envInfo?.mode === "data_plane" ? "Concert Ops" : "Rehearsal Ops";
    const NAV = [
        // iter86.31.14 — Concert-plane landing tab (production only).
        { type: "tab", k: "concert_health", label: "Concert Health", icon: HeartPulse, roles: BOTH },
        { type: "tab", k: "release_ops", label: "Rhythm", icon: AudioWaveform, roles: BOTH },
        {
            type: "group", k: "operations", label: opsLabel, icon: Settings, items: [
                { k: "game_ops", label: "Product Ops", icon: Gamepad2, roles: BOTH },
                // iter87.237 — Tenant-admin/billing-action health (co-admin
                // permission schema drift, Pro+/Paddle integrity). Detector
                // only for now.
                { k: "admin_health", label: "Admin Health", icon: ShieldAlert, roles: BOTH },
                // iter87.65 — Tenant Ops flipped dormant per marie-kondo audit
                // §7 Q2 ratification (single-tenant reality — one tenant, no
                // second-tenant provisioning in the audit window).
                { k: "tenants", label: "Tenant Ops", icon: Globe, roles: ADMIN, dormant: true },
                // iter87.65 — Compliance Ops flipped dormant (zero
                // compliance-state transitions in the audit window; 5
                // obligations, all on multi-month review cadences).
                // iter87.71 — Un-dormanted: Compliance Ops v1 tooling is
                // now live (reject/restore/suppress-only/unsuppress +
                // 7d undo window). Two active obligations un-dormanted
                // in obligations_registry.md §2 (CAN-SPAM, CCPA).
                { k: "compliance_ops", label: "Compliance Ops", icon: ShieldCheck, roles: ADMIN },
                // iter87.105 — Issues queue (in-app "Report an Issue" flow).
                // Sits right after Compliance Ops per operator direction. Badge
                // count derived from `issuesUnread` state (populated on mount +
                // refreshed on tab switch).
                { k: "issues", label: "Issues", icon: MessageCircleWarning, roles: ADMIN, badge: "issuesUnread" },
                // iter87.78.e — Mobile Tester surface hop. `external` opens
                // /tester in a new tab (auth stays separate — Tester has its
                // own AGUID session distinct from platform_admin JWT).
                { k: "mobile_tester", label: "Mobile Tester Sign-in", icon: Smartphone, roles: BOTH, external: "/tester" },
            ],
        },
        // iter87.64 — Pulse retired from default nav (marie-kondo audit §1.3).
        // Reachable via `?tab=pulse` deep-link for archival access to the
        // recorded dispositions. LLM plumbing preserved for repurpose as a
        // workflow-adherence detector — see PULSE_ADHERENCE_DESIGN.md (forthcoming).
        { type: "tab", k: "pulse", label: "Pulse", icon: Radio, roles: BOTH, hiddenFromNav: true },
        {
            type: "group", k: "canon", label: "Canon", icon: ScrollText, items: [
                // iter87.61 Phase 0 · Step 3 — Reference Docs promoted to
                // default landing per COMMONS_MAESTRO_CANON_REDESIGN §M8.
                // iter87.103 — Renamed to "Docs" since the panel now hosts
                // both Reference (current) and Legacy (retired) libraries.
                { k: "docs", label: "Docs", icon: FileText, roles: BOTH },
                // iter87.65 — Atlas flipped dormant per marie-kondo audit
                // §7 Q2 (static architecture diagram; useful for agent
                // onboarding, useless for daily operating).
                { k: "atlas", label: "Atlas", icon: MapIcon, roles: BOTH, dormant: true },
                // iter87.69 — 50-percent-cut experiment: Values, Obligations,
                // Features, Security Posture flipped dormant. Only Rhythm and
                // Vendors stay active during the 30-day observation window.
                // Registry FILES remain untouched (append-only); this hides
                // the read TABS from the console nav.
                { k: "values", label: "Values", icon: Layers, roles: BOTH, dormant: true },
                { k: "obligations", label: "Obligations", icon: Scale, roles: BOTH, dormant: true },
                // iter87.104 — Features un-dormanted at operator request:
                // they use it (with Tech Debt below) as their day-to-day
                // prioritization surface for what to build/fix next.
                { k: "features", label: "Features", icon: BookOpenCheck, roles: BOTH },
                { k: "tech_debt", label: "Tech Debt", icon: Wrench, roles: BOTH },
                { k: "security_posture", label: "Security Posture", icon: ShieldAlert, roles: BOTH, dormant: true },
                // iter87.65 — Developer Agency flipped dormant per marie-kondo
                // audit §7 Q2 (autonomy-matrix has 2 audit rows ever, both
                // smoke-tests from July 5; never tuned in anger).
                { k: "dev_ops", label: "Developer Agency", icon: Bot, roles: BOTH, dormant: true },
            ],
        },
        // iter86.52 — Conductor (née Stewardship) promoted to a main tab
        // next to Canon; rehearsal-plane (preview) only, like Canon/Pulse.
        // iter87.69 — 50-percent-cut experiment: Maestro flipped dormant.
        // All eight Maestro sub-tabs (Actions, Obligations Review, TCL
        // Mapping, Pulse Dispositions, Tech Debt, Vendor Todos, Severity,
        // Audit) sit behind the dormant banner during the 30-day window.
        // Rhythm's Issues queue continues to surface Actions-equivalent
        // items that need operator attention.
        { type: "tab", k: "stewardship", label: "Maestro", icon: Music4, roles: BOTH, dormant: true },
        {
            type: "group", k: "console", label: "Mezzanine", icon: Layers, items: [
                // iter87.65 — Release Notes flipped dormant per marie-kondo
                // audit §7 Q2 (CHANGELOG.md is the real release-notes surface;
                // this tab is a legacy Mongo `release_notes` viewer with no
                // writes in the audit window).
                { k: "releases", label: "Release Notes", icon: Megaphone, roles: ADMIN, dormant: true },
                // iter87.286 (operator-directed) — Vendors stays fully live
                // on the rehearsal plane (preview), where governance writes
                // actually work. On the concert plane (production/beta) —
                // where `require_control_plane` makes the tab read-only and
                // functionally inert anyway (see iter87.284's vendor
                // reconciliation fix) — it's hidden entirely via the
                // `visibleNav` data_plane filter below, not `dormant`
                // (which would hide it on preview too).
                { k: "vendors", label: "Vendors", icon: Handshake, roles: ADMIN },
                // iter87.69 — flipped dormant during the 50-percent-cut window.
                // iter87.76.1 — Un-dormanted: user relies on this surface for
                // live tester-management (invites, revokes, program toggle) and
                // it's the definitive admin plane for the mobile-tester program.
                // Pro+ Subscriptions in Site Admin is a separate concern —
                // testers and Pro+ subscribers are "competing claim spaces"
                // and are managed in different surfaces going forward.
                { k: "mobile_testers", label: "Mobile Testers", icon: Smartphone, roles: ADMIN },
                { k: "platform_roles", label: "Commons Roles", icon: Crown, roles: ["platform_admin"], ownerOnly: true },
                { k: "account", label: "Account", icon: Key, roles: BOTH },
            ],
        },
    ];
    const visibleNav = NAV
        // iter86.31.12/.14 — Rhythm, Pulse + Canon are rehearsal-plane
        // (preview) tools: governance happens where deploys are decided.
        // The production concert plane hides them and lands on the
        // read-only Concert Health tab instead (hidden on preview).
        .filter((n) => envInfo?.mode === "data_plane"
            ? !["pulse", "canon", "release_ops", "stewardship"].includes(n.k)
            : n.k !== "concert_health")
        .map((n) => n.type === "group"
            ? {
                ...n,
                // iter87.286 — Vendors hidden on the concert plane
                // (production/beta) only; stays visible on rehearsal
                // (preview), same asymmetry as the pulse/canon/etc. tabs above.
                items: n.items.filter((i) => i.roles.includes(admin.role) && (!i.ownerOnly || admin.is_super)
                    && !(envInfo?.mode === "data_plane" && i.k === "vendors")),
            }
            : n)
        .filter((n) => n.type === "tab" ? n.roles.includes(admin.role) : n.items.length > 0);
    // iter87.65 — renderedNav strips both:
    //   (a) `hiddenFromNav` tabs (Pulse — reachable only via ?tab= deep-link)
    //   (b) `dormant` items when `showDormant === false` (marie-kondo §7 Q2)
    // `allowedKeys` above still includes every valid key so deep-links to
    // dormant/hidden surfaces render (with a DormantBanner) even when the
    // toggle is off.
    const renderedNav = visibleNav
        .filter((n) => n.type !== "tab" || (!n.hiddenFromNav && (!n.dormant || showDormant)))
        .map((n) => n.type === "group"
            ? { ...n, items: n.items.filter((i) => !i.dormant || showDormant) }
            : n)
        .filter((n) => n.type !== "group" || n.items.length > 0);
    const allowedKeys = visibleNav.flatMap((n) => (n.type === "group" ? n.items.map((i) => i.k) : [n.k]));

    // If somehow the active tab isn't in the allowed list, fall back
    const activeTab = allowedKeys.includes(tab) ? tab : allowedKeys[0];
    // iter87.65 — activeTabIsDormant drives the DormantBanner render above the
    // tab body. Deep-links to dormant surfaces still render (URL persistence
    // wins); the banner names the disposition explicitly.
    const _dormantKeys = new Set(
        visibleNav.flatMap((n) => n.type === "group"
            ? n.items.filter((i) => i.dormant).map((i) => i.k)
            : (n.dormant ? [n.k] : [])),
    );
    const activeTabIsDormant = _dormantKeys.has(activeTab);
    const activeLabel = (k) => {
        for (const n of visibleNav) {
            if (n.type === "tab" && n.k === k) return n.label;
            if (n.type === "group") { const hit = n.items.find((i) => i.k === k); if (hit) return hit.label; }
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-black text-white">
            {/* Commons Console header */}
            <header className="border-b-2 border-volt/30 bg-[#0a0a0a]" data-testid="platform-console-header">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Landmark className="w-6 h-6 text-volt" />
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-volt">
                                Commons Console
                                {envInfo && (
                                    <span
                                        data-testid="platform-env-badge"
                                        title={envInfo.mode === "data_plane"
                                            ? "Production concert plane — live operations; canon is a read-only mirror (govern in the rehearsal plane)"
                                            : "Preview rehearsal plane — governance authoring; decisions flow to git"}
                                        className={`ml-2 pill text-[9px] font-extrabold uppercase border ${envInfo.mode === "data_plane" ? "bg-rose-900/40 text-rose-200 border-rose-700" : "bg-emerald-900/40 text-emerald-200 border-emerald-700"}`}
                                    >
                                        {envInfo.mode === "data_plane"
                                            ? `Concert Plane · ${envInfo.env_label || "production"}`
                                            : "Rehearsal Plane · preview"}
                                    </span>
                                )}
                                {envInfo?.mode === "data_plane" && envInfo.canon_commit && (
                                    <span
                                        data-testid="canon-freshness"
                                        title="The canon this mezzanine shows was baked into this deploy — newer governance decisions in the preview control plane appear here after the next deploy"
                                        className="ml-2 text-[9px] font-bold normal-case tracking-normal text-zinc-400"
                                    >
                                        canon as of <span className="font-mono text-zinc-300">{envInfo.canon_commit}</span>
                                        {envInfo.canon_committed_at ? ` · deployed ${timeAgo(envInfo.canon_committed_at)}` : ""}
                                    </span>
                                )}
                            </p>
                            <p className="text-sm font-bold text-white truncate" data-testid="platform-console-email">
                                {admin.email}
                                <span className={`ml-2 pill text-[9px] font-bold uppercase border ${admin.role === "platform_admin" ? "bg-cyber/15 text-cyber border-cyber/40" : admin.role === "platform_observer" ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" : "bg-volt/15 text-volt border-volt/40"}`} data-testid="platform-console-role-badge">
                                    {admin.role === "platform_admin"
                                        ? (admin.is_super ? "Commons Owner" : "Commons Admin")
                                        : admin.role === "platform_observer" ? "Commons Observer" : "Commons Ops"}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <ConsoleLegend
                            dataPlane={envInfo?.mode === "data_plane"}
                            showDormant={showDormant}
                            onToggleDormant={toggleShowDormant}
                            dormantBusy={dormantBusy}
                        />
                        <a
                            href="/admin"
                            data-testid="goto-tenant-admin-link"
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                            title="Sign in as a tenant Site Admin (separate identity)"
                        >
                            <ShieldCheck className="w-3 h-3" /> Site Admin
                        </a>
                        <button
                            onClick={logout}
                            data-testid="platform-console-signout-btn"
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                        >
                            <LogOut className="w-3 h-3" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
                {/* Top-level nav: standalone tabs + grouped dropdowns */}
                <div className="flex flex-wrap items-center gap-2 border-b-2 border-[#333] pb-3">
                    {renderedNav.map((n) => {
                        const Icon = n.icon;
                        if (n.type === "tab") {
                            const isActive = activeTab === n.k;
                            // iter84.3 — red dot on Rhythm when the most
                            // recent Drift Watch scan found drift.
                            const showDriftDot = n.k === "release_ops" && driftCount > 0;
                            return (
                                <button
                                    key={n.k}
                                    onClick={() => setTab(n.k)}
                                    data-testid={`platform-tab-${n.k}-btn`}
                                    className={`brutal-btn-ghost text-xs py-2 px-3 relative ${isActive ? "border-volt text-volt" : ""}`}
                                >
                                    <Icon className="w-3 h-3" /> {n.label}
                                    {showDriftDot && (
                                        <span
                                            data-testid="platform-tab-release_ops-drift-dot"
                                            title={`${driftCount} tournament(s) with drift — check Drift Watch card`}
                                            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-extrabold rounded-full bg-red-500 text-white border border-black shadow-brutal"
                                        >
                                            {driftCount > 99 ? "99+" : driftCount}
                                        </span>
                                    )}
                                    {n.k === "pulse" && pulseCount > 0 && (
                                        <span
                                            data-testid="platform-tab-pulse-badge"
                                            title={`${pulseCount} unreviewed briefing section(s)`}
                                            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-extrabold rounded-full bg-sky-500 text-white border border-black shadow-brutal"
                                        >
                                            {pulseCount > 99 ? "99+" : pulseCount}
                                        </span>
                                    )}
                                </button>
                            );
                        }
                        const groupActive = n.items.some((i) => i.k === activeTab);
                        // iter87.105 — Group-level badge: sum of any item badges
                        // (currently just Issues under Operations). Renders on
                        // the dropdown trigger so operators see the signal
                        // without opening the menu.
                        const groupBadge = n.items.some((i) => i.k === "issues") ? issuesUnread : 0;
                        return (
                            <DropdownMenu key={n.k}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid={`platform-navgroup-${n.k}-btn`}
                                        className={`brutal-btn-ghost text-xs py-2 px-3 relative ${groupActive ? "border-volt text-volt" : ""}`}
                                    >
                                        <Icon className="w-3 h-3" /> {n.label}
                                        {groupActive && (
                                            <span className="text-[9px] font-bold uppercase text-zinc-400">· {activeLabel(activeTab)}</span>
                                        )}
                                        <ChevronDown className="w-3 h-3 opacity-60" />
                                        {groupBadge > 0 && (
                                            <span
                                                data-testid={`platform-navgroup-${n.k}-badge`}
                                                title={`${groupBadge} unread issue(s)`}
                                                className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-extrabold rounded-full bg-volt text-black border border-black shadow-brutal"
                                            >
                                                {groupBadge > 99 ? "99+" : groupBadge}
                                            </span>
                                        )}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="bg-[#0f0f0f] border-2 border-[#333] text-white min-w-[190px]">
                                    {n.items.map((i) => {
                                        const ItemIcon = i.icon;
                                        const itemActive = activeTab === i.k;
                                        // iter87.78.e — dropdown items with `external` open a
                                        // separate surface in a NEW TAB so the two sessions
                                        // (platform admin JWT + tester AGUID) stay disjoint.
                                        if (i.external) {
                                            return (
                                                <DropdownMenuItem
                                                    key={i.k}
                                                    onClick={() => window.open(i.external, "_blank", "noopener")}
                                                    data-testid={`platform-tab-${i.k}-btn`}
                                                    className="text-xs gap-2 cursor-pointer focus:bg-volt/10 focus:text-volt text-zinc-300"
                                                >
                                                    <ItemIcon className="w-3 h-3" /> {i.label}
                                                    <ExternalLink className="w-3 h-3 opacity-50 ml-auto" />
                                                </DropdownMenuItem>
                                            );
                                        }
                                        // iter87.105 — Per-item badge inside the dropdown.
                                        const itemBadge = i.k === "issues" ? issuesUnread : 0;
                                        return (
                                            <DropdownMenuItem
                                                key={i.k}
                                                onClick={() => setTab(i.k)}
                                                data-testid={`platform-tab-${i.k}-btn`}
                                                className={`text-xs gap-2 cursor-pointer focus:bg-volt/10 focus:text-volt ${itemActive ? "text-volt font-bold" : "text-zinc-300"}`}
                                            >
                                                <ItemIcon className="w-3 h-3" /> {i.label}
                                                {itemBadge > 0 && (
                                                    <span
                                                        data-testid={`platform-tab-${i.k}-badge`}
                                                        className="ml-auto inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-extrabold rounded-full bg-volt text-black"
                                                    >
                                                        {itemBadge > 99 ? "99+" : itemBadge}
                                                    </span>
                                                )}
                                            </DropdownMenuItem>
                                        );
                                    })}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        );
                    })}
                    <button
                        data-testid="platform-refresh-view-btn"
                        onClick={refreshView}
                        disabled={refreshing}
                        title="Refresh the current view — re-pulls this tab's data without reloading the whole page"
                        className="brutal-btn-ghost text-xs py-2 px-3 ml-auto text-zinc-400 hover:text-volt disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>

                {/* iter86.31.15 — Read-only affordance for Commons roles.
                    Backend write-guard is the real enforcement; this chip
                    just sets expectations before a 403 does. */}
                {(admin.role === "platform_observer"
                    || (admin.role === "platform_developer"
                        && ["pulse", "atlas", "values", "obligations", "features", "security_posture", "stewardship", "dev_ops", "docs", "releases", "vendors"].includes(activeTab))) && (
                    <div data-testid="commons-readonly-banner" className="mb-4 px-3 py-2 border-2 border-amber-600/50 bg-amber-950/30 rounded-md text-[11px] font-bold text-amber-300 flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 shrink-0" />
                        READ-ONLY — your Commons role can view this area but not modify it.
                    </div>
                )}

                {/* Tab content — keyed wrapper so the Refresh button can
                    remount (and therefore refetch) the active view. */}
                <div key={viewKey} className="space-y-4" data-testid="platform-view-container">
                {/* iter87.65 — Dormant status banner. Renders above ANY
                    dormant tab body (deep-linked or revealed via toggle) so
                    the operator sees the disposition before they act. */}
                {activeTabIsDormant && <DormantBanner />}
                {activeTab === "concert_health" && <ConcertHealthTab onOpenGameOps={(t) => { setGameOpsTarget(t && t.sub ? t : null); setTab("game_ops"); }} />}
                {activeTab === "release_ops" && <ReleaseOpsTab viewerRole={admin.role} onOpenGameOps={(t) => { setGameOpsTarget(t && t.sub ? t : null); setTab("game_ops"); }} />}
                {activeTab === "pulse" && <PulseTab onBadgeChange={setPulseCount} />}
                {activeTab === "tenants" && <PlatformTenantsTab />}
                {activeTab === "compliance_ops" && <ComplianceTab scope="platform" />}
                {activeTab === "values" && <ValuesTab />}
                {activeTab === "atlas" && <AtlasTab onNavigate={(k) => setTab(k)} />}
                {activeTab === "obligations" && <ObligationsTab />}
                {activeTab === "features" && <FeaturesTab onJumpToVendor={(name) => { setVendorsTarget(name); setTab("vendors"); }} />}
                {activeTab === "tech_debt" && <PlatformTechDebtTab />}
                {activeTab === "issues" && <IssuesQueueCard />}
                {activeTab === "security_posture" && <SecurityPostureTab />}
                {activeTab === "game_ops" && <GameOpsTab initialTarget={gameOpsTarget} />}
                {activeTab === "admin_health" && <AdminHealthTab />}
                {activeTab === "stewardship" && <StewardshipTab />}
                {activeTab === "dev_ops" && <DevOpsTab viewerRole={admin.role} />}
                {activeTab === "docs" && <DocsTab />}
                {activeTab === "releases" && <ReleaseNotesTab />}
                {activeTab === "vendors" && (
                    <div className={envInfo?.mode === "data_plane" ? "pointer-events-none opacity-60" : ""} data-testid="console-vendors-tab">
                        <VendorRegistrySection readOnly={envInfo?.mode === "data_plane"} highlightVendor={vendorsTarget} onHighlightDone={() => setVendorsTarget(null)} />
                    </div>
                )}
                {activeTab === "platform_roles" && <PlatformRolesTab />}
                {activeTab === "mobile_testers" && <MobileTestersTab />}
                {activeTab === "account" && <AccountTab admin={admin} onPasswordChanged={logout} />}
                </div>
            </main>
        </div>
    );
}

function AccountTab({ admin, onPasswordChanged }) {
    const [oldPw, setOldPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
        setBusy(true);
        try {
            await api.authChangePassword({ current_password: oldPw, new_password: newPw });
            toast.success("Password updated — please sign in again");
            onPasswordChanged();
        } catch (err) {
            toast.error(_extractErr(err, "Failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="platform-console-account-tab">
            <div className="brutal-card p-4">
                <p className="text-[10px] font-bold uppercase text-zinc-400 tracking-widest mb-2">Identity</p>
                <p className="text-sm font-bold text-white">{admin.email}</p>
                <p className="text-xs text-zinc-500 mt-1" data-testid="account-identity-role">
                    {admin.is_super ? "Commons Owner"
                        : admin.role === "platform_admin" ? "Commons Admin"
                        : admin.role === "platform_developer" ? "Commons Ops"
                        : admin.role === "platform_observer" ? "Commons Observer"
                        : (admin.name || "Platform user")}
                    <span className="text-zinc-600"> · internal key {admin.role}</span>
                </p>
            </div>

            <div className="brutal-card p-4">
                <p className="text-[10px] font-bold uppercase text-zinc-400 tracking-widest mb-3">Change password</p>
                <form onSubmit={submit} className="space-y-2 max-w-md">
                    <input
                        type="password" placeholder="Current password" required
                        value={oldPw} onChange={(e) => setOldPw(e.target.value)}
                        data-testid="account-old-password-input"
                        className="brutal-input w-full"
                    />
                    <input
                        type="password" placeholder="New password (min 8 chars)" required minLength={8}
                        value={newPw} onChange={(e) => setNewPw(e.target.value)}
                        data-testid="account-new-password-input"
                        className="brutal-input w-full"
                    />
                    <button type="submit" disabled={busy} data-testid="account-change-password-btn" className="brutal-btn">
                        {busy ? "Updating…" : "Update password"}
                    </button>
                </form>
                <TotpCard />
            </div>
        </div>
    );
}
